import * as React from "react";

import { type ContextType } from "../contextType";

import { ContextChip } from "./ContextChip";
import type { ContextIconKind } from "./ContextIcon";
import { MoreContextChip } from "./MoreContextChip";

export interface ContextItem {
  id: string;
  label: string;
  tooltip?: string;
  /**
   * Icon to show instead of the group's, for an item that stands for
   * something other than the group's type: the View loaded on a table, or a
   * section or panel selected on a board's canvas.
   */
  icon?: ContextIconKind;
}

interface ContextGroupProps {
  contextType: ContextType;
  items: ContextItem[];
  onDelete: (id: string) => void;
}

/**
 * Groups context chips by type with smart collapsing:
 * - 1 item: show just that chip
 * - 2 items: show both chips
 * - 3+ items: show first chip + "N more..." chip (collapsed by default)
 * - When expanded: show all chips
 */
export const ContextGroup = ({
  contextType,
  items,
  onDelete,
}: ContextGroupProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  React.useEffect(
    function collapseWhenItemsChange() {
      if (items.length <= 2 && isExpanded) {
        setIsExpanded(false);
      }
    },
    [items.length, isExpanded],
  );

  if (items.length === 0) {
    return null;
  }

  // 1 or 2 items: always show all
  if (items.length <= 2) {
    return items.map((item) => (
      <ContextChip
        key={item.id}
        contextType={contextType}
        label={item.label}
        tooltip={item.tooltip}
        icon={item.icon}
        onDelete={() => onDelete(item.id)}
      />
    ));
  }

  // 3+ items: show first + "more" chip when collapsed
  if (!isExpanded) {
    const firstItem = items[0];
    const remainingCount = items.length - 1;

    return (
      <>
        <ContextChip
          key={firstItem.id}
          contextType={contextType}
          label={firstItem.label}
          tooltip={firstItem.tooltip}
          icon={firstItem.icon}
          onDelete={() => onDelete(firstItem.id)}
        />
        <MoreContextChip
          count={remainingCount}
          contextType={contextType}
          onClick={() => setIsExpanded(true)}
        />
      </>
    );
  }

  // Expanded: show all chips
  return items.map((item) => (
    <ContextChip
      key={item.id}
      contextType={contextType}
      label={item.label}
      tooltip={item.tooltip}
      icon={item.icon}
      onDelete={() => onDelete(item.id)}
    />
  ));
};
